/**
 * useTranscriber — unified transcription hook
 *
 * Desktop: uses local Whisper WASM worker (free, offline)
 * iOS/mobile: uses server-side OpenAI Whisper API ($0.006/min)
 *
 * Both paths expose the same interface: load, transcribe, status, progress
 */

import { useState, useRef, useCallback, useEffect } from "react";

export type ModelStatus = "idle" | "loading" | "ready" | "error";

interface TranscriberState {
  modelStatus: ModelStatus;
  loadProgress: number;
  error: string | null;
  isIOS: boolean;
}

interface UseTranscriberReturn extends TranscriberState {
  loadModel: () => void;
  transcribe: (audioData: Float32Array) => void;
  onResult: (callback: (text: string, time: string) => void) => void;
}

function detectIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1) // iPad with desktop UA
  );
}

/**
 * Convert Float32Array (PCM 16kHz) to WAV blob for server upload
 */
function float32ToWav(samples: Float32Array, sampleRate = 16000): Blob {
  const numChannels = 1;
  const bitsPerSample = 16;
  const byteRate = sampleRate * numChannels * (bitsPerSample / 8);
  const blockAlign = numChannels * (bitsPerSample / 8);
  const dataSize = samples.length * (bitsPerSample / 8);
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  // WAV header
  const writeStr = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
  };
  writeStr(0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeStr(8, "WAVE");
  writeStr(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitsPerSample, true);
  writeStr(36, "data");
  view.setUint32(40, dataSize, true);

  // Convert float32 to int16
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }

  return new Blob([buffer], { type: "audio/wav" });
}

export function useTranscriber(): UseTranscriberReturn {
  const [modelStatus, setModelStatus] = useState<ModelStatus>("idle");
  const [loadProgress, setLoadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const workerRef = useRef<Worker | null>(null);
  const resultCallbackRef = useRef<((text: string, time: string) => void) | null>(null);
  const isIOS = detectIOS();

  // Set up result callback
  const onResult = useCallback((callback: (text: string, time: string) => void) => {
    resultCallbackRef.current = callback;
  }, []);

  // Initialize worker (desktop only)
  useEffect(() => {
    if (isIOS) return; // Skip worker on iOS

    const worker = new Worker("/whisper-worker.js", { type: "module" });
    worker.onmessage = (e) => {
      const { type, text, time, progress, status, error: err } = e.data;
      if (type === "progress") {
        setLoadProgress(progress ?? 0);
        if (status === "ready") setModelStatus("ready");
      }
      if (type === "ready") setModelStatus("ready");
      if (type === "result" && resultCallbackRef.current) {
        resultCallbackRef.current(text, time);
      }
      if (type === "error") {
        setError(err);
        setModelStatus("error");
      }
    };
    workerRef.current = worker;

    return () => worker.terminate();
  }, [isIOS]);

  const loadModel = useCallback(() => {
    if (isIOS) {
      // Server-side — no model to load, just mark ready
      setModelStatus("ready");
      setLoadProgress(100);
      return;
    }
    if (workerRef.current && modelStatus === "idle") {
      setModelStatus("loading");
      workerRef.current.postMessage({ type: "load" });
    }
  }, [isIOS, modelStatus]);

  const transcribe = useCallback(
    async (audioData: Float32Array) => {
      if (isIOS) {
        // Server-side transcription
        const t0 = performance.now();
        try {
          const wavBlob = float32ToWav(audioData);
          const formData = new FormData();
          formData.append("file", wavBlob, "audio.wav");

          const res = await fetch("/api/transcribe", {
            method: "POST",
            body: formData,
          });

          if (!res.ok) {
            const err = await res.text();
            setError(`Server transcription failed: ${err}`);
            return;
          }

          const data = await res.json();
          const elapsed = ((performance.now() - t0) / 1000).toFixed(1);
          if (data.text && resultCallbackRef.current) {
            resultCallbackRef.current(data.text.trim(), elapsed);
          }
        } catch (err) {
          setError(err instanceof Error ? err.message : "Transcription failed");
        }
        return;
      }

      // Local Whisper worker
      if (!workerRef.current) return;
      workerRef.current.postMessage({ type: "transcribe", audio: audioData });
    },
    [isIOS]
  );

  return {
    modelStatus,
    loadProgress,
    error,
    isIOS,
    loadModel,
    transcribe,
    onResult,
  };
}
